import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Smartphone,
  MapPin,
  Battery,
  Wifi,
  Clock,
  AlertTriangle,
  FileText,
  Copy,
  Check,
  Activity,
  Zap,
  Code,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { formatDistanceToNow, format, isValid } from 'date-fns';
import { apiClient, CrashDetail as CrashDetailType } from '../api/client';
import { AppBadge } from '../components/AppFlavor';

function safeFormat(dateStr: string | undefined | null, pattern: string): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return isValid(d) ? format(d, pattern) : '—';
}

function safeFromNow(dateStr: string | undefined | null): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return isValid(d) ? formatDistanceToNow(d, { addSuffix: true }) : '—';
}

export default function CrashDetail() {
  const { crashId } = useParams<{ crashId: string }>();
  const [crash, setCrash] = useState<CrashDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    stackTrace: true,
    componentStack: true,
    customData: false,
  });

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  useEffect(() => {
    async function fetchCrash() {
      if (!crashId) return;
      try {
        const data = await apiClient.getCrashDetail(crashId);
        setCrash(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load crash');
      } finally {
        setLoading(false);
      }
    }

    fetchCrash();
  }, [crashId]);

  const handleStatusUpdate = async (newStatus: string) => {
    if (!crashId) return;
    setUpdating(true);
    try {
      await apiClient.updateCrash(crashId, { status: newStatus });
      setCrash((prev) =>
        prev ? { ...prev, crash: { ...prev.crash, status: newStatus } } : null
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const copyStackTrace = () => {
    if (crash?.crash.stack_trace) {
      navigator.clipboard.writeText(crash.crash.stack_trace);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-48" />
          <div className="h-64 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  if (error || !crash) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error || 'Crash not found'}</p>
        </div>
      </div>
    );
  }

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'new':
        return 'badge-error';
      case 'investigating':
        return 'badge-warning';
      case 'resolved':
        return 'badge-success';
      case 'ignored':
        return 'badge-neutral';
      default:
        return 'badge-neutral';
    }
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/crashes"
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeft size={16} className="mr-1" />
          Back to crashes
        </Link>

        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <AlertTriangle className="text-red-500" size={24} />
              <h1 className="text-2xl font-bold text-gray-900">
                {crash.crash.error_name || 'Error'}
              </h1>
              <span className={`badge ${getStatusBadgeClass(crash.crash.status)}`}>
                {crash.crash.status}
              </span>
            </div>
            <p className="text-gray-600">{crash.crash.error_message}</p>
          </div>

          {/* Status actions */}
          <div className="flex gap-2">
            {crash.crash.status !== 'investigating' && (
              <button
                onClick={() => handleStatusUpdate('investigating')}
                disabled={updating}
                className="px-4 py-2 bg-yellow-100 text-yellow-800 rounded-lg hover:bg-yellow-200 transition-colors disabled:opacity-50"
              >
                Investigate
              </button>
            )}
            {crash.crash.status !== 'resolved' && (
              <button
                onClick={() => handleStatusUpdate('resolved')}
                disabled={updating}
                className="px-4 py-2 bg-green-100 text-green-800 rounded-lg hover:bg-green-200 transition-colors disabled:opacity-50"
              >
                Resolve
              </button>
            )}
            {crash.crash.status !== 'ignored' && (
              <button
                onClick={() => handleStatusUpdate('ignored')}
                disabled={updating}
                className="px-4 py-2 bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                Ignore
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Crash Info */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <AlertTriangle size={20} />
            Crash Info
          </h2>
          <dl className="space-y-3">
            <div>
              <dt className="text-xs text-gray-500">Type</dt>
              <dd className="text-sm font-medium text-gray-900">{crash.crash.error_type}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Occurred</dt>
              <dd className="text-sm text-gray-900">
                {safeFormat(crash.crash.occurred_at, 'PPpp')}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Reported</dt>
              <dd className="text-sm text-gray-900">
                {safeFromNow(crash.crash.reported_at)}
              </dd>
            </div>
            {crash.crash.screen_name && (
              <div>
                <dt className="text-xs text-gray-500">Screen</dt>
                <dd className="text-sm text-gray-900">{crash.crash.screen_name}</dd>
              </div>
            )}
            {crash.crash.action && (
              <div>
                <dt className="text-xs text-gray-500">Action</dt>
                <dd className="text-sm text-gray-900 font-mono bg-gray-100 px-2 py-1 rounded">
                  {crash.crash.action}
                </dd>
              </div>
            )}
          </dl>
        </div>

        {/* Device Info */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Smartphone size={20} />
            Device
          </h2>
          <dl className="space-y-3">
            <div>
              <dt className="text-xs text-gray-500">Platform</dt>
              <dd className="text-sm text-gray-900">
                {crash.device.platform === 'ios' ? 'iOS' : 'Android'}{' '}
                {crash.crash.os_version}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Model</dt>
              <dd className="text-sm text-gray-900">{crash.crash.device_model}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">App Version</dt>
              <dd className="text-sm text-gray-900 flex items-center gap-2">
                v{crash.crash.app_version}
                <AppBadge flavor={crash.crash.app_flavor} />
              </dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">User</dt>
              <dd className="text-sm text-gray-900">
                <Link
                  to={`/users/${crash.device.id}`}
                  className="text-primary-600 hover:text-primary-700"
                >
                  {crash.device.email || crash.device.id.slice(0, 8) + '...'}
                </Link>
              </dd>
            </div>
          </dl>
        </div>

        {/* Device State */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Activity size={20} />
            State at Crash
          </h2>
          <div className="space-y-3">
            {crash.crash.battery_level != null && (
              <div className="flex items-center gap-2">
                <Battery size={16} className="text-gray-400" />
                <span className="text-sm text-gray-600">
                  {Math.round(crash.crash.battery_level * 100)}% battery
                  {crash.crash.is_charging && (
                    <span className="ml-1 text-green-600">
                      <Zap size={12} className="inline" /> Charging
                    </span>
                  )}
                </span>
              </div>
            )}
            {crash.crash.memory_usage != null && (
              <div className="flex items-center gap-2">
                <Activity size={16} className="text-gray-400" />
                <span className="text-sm text-gray-600">
                  {crash.crash.memory_usage.toFixed(1)} MB memory
                </span>
              </div>
            )}
            {(crash.crash.network_type || crash.session?.connection_type) && (
              <div className="flex items-center gap-2">
                <Wifi size={16} className="text-gray-400" />
                <span className="text-sm text-gray-600">
                  {crash.crash.network_type || crash.session?.connection_type}
                </span>
              </div>
            )}
            {crash.crash.location?.latitude != null &&
              crash.crash.location?.longitude != null && (
                <div className="flex items-center gap-2">
                  <MapPin size={16} className="text-gray-400" />
                  <span className="text-sm text-gray-600">
                    {crash.crash.location.latitude.toFixed(4)},{' '}
                    {crash.crash.location.longitude.toFixed(4)}
                    {crash.crash.is_moving && ' (Moving)'}
                    {crash.crash.location.speed != null &&
                      ` @ ${(crash.crash.location.speed * 3.6).toFixed(1)} km/h`}
                  </span>
                </div>
              )}
            {crash.session && (
              <>
                <div className="flex items-center gap-2">
                  <Clock size={16} className="text-gray-400" />
                  <span className="text-sm text-gray-600">
                    Session started {safeFromNow(crash.session.started_at)}
                  </span>
                </div>
                {crash.session.initial_route && (
                  <div className="flex items-center gap-2">
                    <Code size={16} className="text-gray-400" />
                    <span className="text-sm text-gray-600">
                      Started on:{' '}
                      <span className="font-mono">{crash.session.initial_route}</span>
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Custom Data */}
      {crash.crash.custom_data && Object.keys(crash.crash.custom_data).length > 0 && (
        <div className="card mt-6">
          <button
            onClick={() => toggleSection('customData')}
            className="flex items-center justify-between w-full text-left"
          >
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Code size={20} />
              Custom Data
            </h2>
            {expandedSections.customData ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
          </button>
          {expandedSections.customData && (
            <pre className="mt-4 p-4 bg-gray-50 rounded-lg text-sm overflow-x-auto">
              {JSON.stringify(crash.crash.custom_data, null, 2)}
            </pre>
          )}
        </div>
      )}

      {/* Stack Trace */}
      {crash.crash.stack_trace && (
        <div className="card mt-6">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => toggleSection('stackTrace')}
              className="flex items-center gap-2 text-lg font-semibold text-gray-900"
            >
              {expandedSections.stackTrace ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
              <FileText size={20} />
              Stack Trace
              <span className="text-sm font-normal text-gray-500">
                ({crash.crash.stack_trace.split('\n').length} lines)
              </span>
            </button>
            <button
              onClick={copyStackTrace}
              className="flex items-center gap-1 px-3 py-1 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg"
            >
              {copied ? <Check size={16} /> : <Copy size={16} />}
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
          {expandedSections.stackTrace && (
            <pre className="stack-trace max-h-96 overflow-y-auto">{crash.crash.stack_trace}</pre>
          )}
        </div>
      )}

      {/* Component Stack */}
      {crash.crash.component_stack && (
        <div className="card mt-6">
          <button
            onClick={() => toggleSection('componentStack')}
            className="flex items-center gap-2 text-lg font-semibold text-gray-900 w-full text-left"
          >
            {expandedSections.componentStack ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
            Component Stack
            <span className="text-sm font-normal text-gray-500">
              ({crash.crash.component_stack.split('\n').length} components)
            </span>
          </button>
          {expandedSections.componentStack && (
            <pre className="stack-trace mt-4 max-h-64 overflow-y-auto">{crash.crash.component_stack}</pre>
          )}
        </div>
      )}

      {/* Breadcrumbs */}
      {crash.breadcrumbs && crash.breadcrumbs.length > 0 && (
        <div className="card mt-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Breadcrumbs</h2>
          <div className="space-y-2">
            {crash.breadcrumbs.map((crumb, index) => (
              <div
                key={index}
                className="flex items-center gap-4 p-2 bg-gray-50 rounded text-sm"
              >
                <span className="text-xs text-gray-400 whitespace-nowrap">
                  {safeFormat(crumb.timestamp, 'HH:mm:ss')}
                </span>
                <span className="badge badge-neutral">{crumb.type}</span>
                <span className="text-gray-700 flex-1 truncate">{crumb.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Similar Crashes */}
      {crash.similar_crashes && crash.similar_crashes.length > 0 && (
        <div className="card mt-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Similar Crashes ({crash.similar_crashes.length})
          </h2>
          <div className="space-y-2">
            {crash.similar_crashes.slice(0, 10).map((similar) => (
              <Link
                key={similar.id}
                to={`/crashes/${similar.id}`}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <span className="text-sm text-gray-600">
                  Device: {similar.device_id.slice(0, 8)}...
                </span>
                <span className="text-sm text-gray-500">
                  {safeFromNow(similar.occurred_at)}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
